import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { EmailRequestType, MessagingPlatform } from '@this+that/models';
import log from 'loglevel';

import { useGetUserCommunicationById } from '../../../services/userCommunications.service';
import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import {
  CommunicationReportItemActionKind,
  useCommunicationReportActionsStore,
} from '../../../store/CommunicationReportActions';
import { useCustomToast } from '../../useCustomToast';

interface UseReportItemEmailActionResult {
  handleArchive: (messageId: string) => Promise<void>;
  handleDelete: (messageId: string) => Promise<void>;
  handleUndo: (messageId: string) => void;
}

function buildModifyEmailPayload(
  userEmailAddress: string,
  platform: MessagingPlatform,
  messageId: string,
  action: EmailRequestType.ARCHIVE | EmailRequestType.TRASH,
) {
  return { userEmailAddress, platform, messageId, action };
}

export function useReportItemEmailAction(): UseReportItemEmailActionResult {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();
  const { getUserCommunicationById } = useGetUserCommunicationById();
  const {
    markActionPending,
    markActionCompleted,
    clearActionPending,
    resetItem,
  } = useCommunicationReportActionsStore();

  const fetchCommunicationForAction = useCallback(
    async (messageId: string, errorKey: string) => {
      const communication = await getUserCommunicationById(messageId);
      if (!communication) {
        log.error(
          `[useReportItemEmailAction] Communication not found for messageId: ${messageId}`,
        );
        errorMessage(t(errorKey));
        clearActionPending(messageId);
        return null;
      }
      if (!communication.userEmailAddress || !communication.platform) {
        log.error(
          `[useReportItemEmailAction] Missing userEmailAddress or platform for messageId: ${messageId}`,
        );
        errorMessage(t(errorKey));
        clearActionPending(messageId);
        return null;
      }
      return communication;
    },
    [getUserCommunicationById, errorMessage, t, clearActionPending],
  );

  const handleArchive = useCallback(
    async (messageId: string) => {
      markActionPending(messageId);
      const communication = await fetchCommunicationForAction(
        messageId,
        'assistant.communicationReport.actions.archiveError',
      );
      if (!communication) {
        return;
      }
      try {
        await modifyEmail(
          buildModifyEmailPayload(
            communication.userEmailAddress!,
            communication.platform!,
            messageId,
            EmailRequestType.ARCHIVE,
          ),
        );
        markActionCompleted(
          messageId,
          CommunicationReportItemActionKind.ARCHIVED,
        );
      } catch (error) {
        log.error(
          `[useReportItemEmailAction] Archive failed for ${messageId}:`,
          error,
        );
        clearActionPending(messageId);
        errorMessage(t('assistant.communicationReport.actions.archiveError'));
      }
    },
    [
      markActionPending,
      fetchCommunicationForAction,
      markActionCompleted,
      clearActionPending,
      errorMessage,
      t,
    ],
  );

  const handleDelete = useCallback(
    async (messageId: string) => {
      markActionPending(messageId);
      const communication = await fetchCommunicationForAction(
        messageId,
        'assistant.communicationReport.actions.deleteError',
      );
      if (!communication) {
        return;
      }
      try {
        await modifyEmail(
          buildModifyEmailPayload(
            communication.userEmailAddress!,
            communication.platform!,
            messageId,
            EmailRequestType.TRASH,
          ),
        );
        markActionCompleted(
          messageId,
          CommunicationReportItemActionKind.DELETED,
        );
      } catch (error) {
        log.error(
          `[useReportItemEmailAction] Delete failed for ${messageId}:`,
          error,
        );
        clearActionPending(messageId);
        errorMessage(t('assistant.communicationReport.actions.deleteError'));
      }
    },
    [
      markActionPending,
      fetchCommunicationForAction,
      markActionCompleted,
      clearActionPending,
      errorMessage,
      t,
    ],
  );

  const handleUndo = useCallback(
    (messageId: string) => {
      resetItem(messageId);
    },
    [resetItem],
  );

  return { handleArchive, handleDelete, handleUndo };
}
